import React from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { Clear, Refresh } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { ErrorContainedButton, TextCancelButton } from '../button';
import { acme, acmePageSectionCardSx, acmeBillingSectionContentSx } from '../../theme/acmeTheme';
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  Card,
  CardContent,
  Input,
  Button,
  IconButton,
  Dialog,
  DialogTitleText,
  DialogBody,
  DialogFooter,
} from '../ui';
import PageSnackbars from '../common/PageSnackbars';
import DomainPasswordDialog from '../DomainPasswordDialog';
import DomainDetailsDialog from './DomainDetailsDialog';
import DnsRecordInfoDialog from './DnsRecordInfoDialog';
import DomainsTable from './DomainsTable';
import { useDomains } from './useDomains';

const Domains = () => {
  const {
    domain,
    setDomain,
    domains,
    selectedDomain,
    detailsOpen,
    confirmOpen,
    snackbarProps,
    refreshingRatings,
    refreshingAll,
    checkingDomains,
    flushingCache,
    passwordDialogOpen,
    pendingDomain,
    handleRefreshDomainRatings,
    handleRefreshAllRatings,
    handleAddDomain,
    handlePasswordSuccess,
    handlePasswordDialogClose,
    handleOpenDialog,
    handleCloseDialog,
    handleOpenConfirmDialog,
    handleCloseConfirmDialog,
    handleDeleteDomain,
    copyToClipboard,
    handleCheckAllRecords,
    handleFlushDnsCache,
    infoDialogOpen,
    currentRecordType,
    handleOpenInfoDialog,
    handleCloseInfoDialog,
  } = useDomains();

  return (
    <PageShell>
      <PageHeader
        title="Verified Domains"
        description="Add domains and configure SPF, DKIM, DMARC and BIMI for deliverability."
        sx={{ mb: 2 }}
        action={
          <Box display="flex" gap={0.5}>
            <Tooltip title="Clear DNS Cache & Restart Services">
              <IconButton onClick={handleFlushDnsCache} disabled={flushingCache} aria-label="Clear DNS cache">
                {flushingCache ? <CircularProgress size={18} /> : <Clear fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh all ratings">
              <IconButton onClick={handleRefreshAllRatings} disabled={refreshingAll} aria-label="Refresh all ratings">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none', mt: 0 }}>
        <CardContent sx={acmeBillingSectionContentSx}>
          <PageBlockTitle gutterBottom>Verified Domains</PageBlockTitle>

      <Box sx={{ mb: 2 }}>
          <Input
            label="example.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button variant="default" onClick={handleAddDomain} startIcon={<AddIcon />} fullWidth>
            Add Domain
          </Button>
      </Box>

      <DomainsTable
        domains={domains}
        refreshingRatings={refreshingRatings}
        checkingDomains={checkingDomains}
        onRefreshDomainRatings={handleRefreshDomainRatings}
        onOpenDetails={handleOpenDialog}
        onCheckAllRecords={handleCheckAllRecords}
        onOpenConfirmDelete={handleOpenConfirmDialog}
      />

        </CardContent>
      </Card>

      {selectedDomain && (
        <DomainDetailsDialog
          open={detailsOpen}
          onClose={handleCloseDialog}
          selectedDomain={selectedDomain}
          refreshingRatings={refreshingRatings}
          onRefreshDomainRatings={handleRefreshDomainRatings}
          copyToClipboard={copyToClipboard}
          onOpenInfoDialog={handleOpenInfoDialog}
        />
      )}

      <Dialog open={confirmOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitleText>Confirm Delete</DialogTitleText>
        <DialogBody>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
            Are you sure you want to delete the domain <strong>{selectedDomain?.domain}</strong>?
          </Typography>
        </DialogBody>
        <DialogFooter>
          <TextCancelButton onClick={handleCloseConfirmDialog}>Cancel</TextCancelButton>
          <ErrorContainedButton onClick={handleDeleteDomain}>Delete</ErrorContainedButton>
        </DialogFooter>
      </Dialog>

      <DnsRecordInfoDialog
        open={infoDialogOpen}
        onClose={handleCloseInfoDialog}
        recordType={currentRecordType}
      />

      <DomainPasswordDialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        onSuccess={handlePasswordSuccess}
        domain={pendingDomain}
        action="add domain"
      />

      <PageSnackbars {...snackbarProps} />
    </PageShell>
  );
};

export default Domains;
